import React, { ComponentType, useMemo, useRef, useState } from 'react'
import { IDashboard, IWidget } from '@/pages/Dashboards/dashboards.types.ts'
import { CardMoveType } from '@/pages/Dashboards/services/autoalign/autoalign.types.ts'
import ChartErrorBoundary from '@/pages/Dashboards/Charts/lib/ChartErrorBoundary'
import css from '../widgetCard.module.css'
import DragIcon from '@/assets/icons/drag.svg?react'
import useDataRef, { IUseDataRefProps } from '@/services/data/hooks/useDataRef.ts'
import usePlayer from '@/pages/Dashboards/services/player/usePlayer.ts'
import dashboardsUtils from '@/pages/Dashboards/dashboards.utils.ts'
import LineChart from '@/pages/Dashboards/Charts/LineChart'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'
import usePopup from '@/shared/hooks/usePopup.ts'
import DropdownUnderThreeDots from '@/shared/Dropdowns/DropdownUnderThreeDots'
import DropdownListItemDelete from '@/shared/Dropdowns/DropdownListItem/DropdownListItemDelete'
import confirmService from '@/services/ConfirmService/confirm.service.ts'
import dashboardsApi from '@/pages/Dashboards/dashboards.api.ts'
import dashboardsEvents from '@/pages/Dashboards/events/dashboards.events.ts'
import DropdownListItemSettings from '@/shared/Dropdowns/DropdownListItem/DropdownListItemSettings'
import UpdateWidgetSidePage from '@/pages/Dashboards/components/UpdateWidgetSidePage'
import CardPositionControls from '@/pages/Dashboards/pages/DashboardScreen/WidgetCard/CardPositionControls'

const components: { [type in IWidget['type']]?: ComponentType<any> } = {
  linear: LineChart,
}

interface IProps {
  dashboardId: IDashboard['id']
  component: IWidget
  onMove: (position: IWidget, type: CardMoveType) => void
  onMoveFinish: () => void
  viewport: number
}

const IntervalValuesWidgetCard = (props: IProps) => {
  const [lastResize, setResize] = useState(0)
  const [updateModalOpen, setUpdateModalOpen] = useState(false)

  const cardRef = useRef<HTMLDivElement>()
  const menuRef = useRef<HTMLDivElement>()
  const { open: menuOpen, setOpen: setMenuOpen } = usePopup(menuRef)

  const player = usePlayer()

  const widgetDataConfig: IUseDataRefProps = useMemo(() => {
    const devicesAndParameters = dashboardsUtils.getWidgetDataConfig(props.component)

    return {
      initialTsFrom: player.tsFrom,
      initialTsTo: player.tsTo,
      live: player.live,
      keepLastValue: true,
      ...devicesAndParameters,
    }
  }, [props.component, player])

  const handleDelete = () => {
    confirmService.requestConfirm({
      description: `Подтвердите удаление виджета"`,
      buttonType: 'primary',
      onConfirm: () =>
        dashboardsApi
          .deleteWidget(props.component.id)
          .then((r) => r.status === 'success' && dashboardsEvents.widgetDeleted(r.body.id)),
    })
  }

  const dataRef = useDataRef(widgetDataConfig)

  const Component: any = components[props.component.type]

  const renderContent = () => {
    if (!dataRef.dataRef || dataRef.loading) {
      return (
        <div className={css.loaderWrapper}>
          <ThreeDotsLoader />
        </div>
      )
    }

    return (
      <div style={{ flex: 1 }}>
        <Component widget={props.component} dashboard={props.component.dashboardId} dataRef={dataRef.dataRef} />
      </div>
    )
  }

  return (
    <div
      ref={cardRef}
      className={css.wrapper}
      onContextMenu={(ev) => {
        ev.stopPropagation()
        ev.preventDefault()

        setMenuOpen(true)
      }}
    >
      <div className="flex-space-between">
        <div className="inline-flex-gap gap4 center">
          <DragIcon style={{ cursor: 'grab', width: '32px', height: '32px', padding: '8px' }} />
          <p className="text-nm">{(props.component.configuration.settings as any).title}</p>
        </div>
        <DropdownUnderThreeDots open={menuOpen} setOpen={setMenuOpen} ref={menuRef} position="left">
          <DropdownListItemSettings text="Настройки" onClick={() => setUpdateModalOpen(true)} />
          <DropdownListItemDelete text="Удалить" onClick={handleDelete} />
        </DropdownUnderThreeDots>
      </div>
      <ChartErrorBoundary>{renderContent()}</ChartErrorBoundary>
      <CardPositionControls {...props} cardRef={cardRef} onResize={() => setResize(lastResize + 1)} />
      <UpdateWidgetSidePage widget={props.component} open={updateModalOpen} onClose={() => setUpdateModalOpen(false)} />
    </div>
  )
}

export default React.memo(IntervalValuesWidgetCard)
